<template>
  <b-container class="mt-5">
    <b-row>
      <b-col cols="12" md="7" lg="5" xl="4" class="mx-auto auth-card text-center">
        <b-overlay :show="verifying" rounded="sm">
          <Logo :logoProps="logo" class="mb-4" />

          <div v-if="!verifying">
            <h4 class="mt-4 mb-1">Reset Password</h4>

            <p v-if="linkInvalid" class="mt-3 mb-0">
              Invalid url. Please use the "forgot password" button on the
              <router-link to="/">login</router-link> page to receive a new link.
            </p>

            <template v-else>
              <p class="text-muted mb-3" v-if="username">for {{ username }}</p>

              <b-form
                @submit.stop.prevent="onSubmit"
                class="forms text-left"
              >
                <b-form-group class="mb-2">
                  <b-form-input
                    type="password"
                    v-model="$v.form.newPassword.$model"
                    placeholder="New Password"
                    :state="$v.form.newPassword.$dirty ? !$v.form.newPassword.$error : null"
                  />
                </b-form-group>

                <ul class="password-rules list-unstyled">
                  <li :class="{ 'rule-met': passwordRules.minLength }">
                    <span class="rule-icon">{{ passwordRules.minLength ? "✓" : "✗" }}</span>
                    Minimum 10 characters
                  </li>
                  <li :class="{ 'rule-met': passwordRules.upper }">
                    <span class="rule-icon">{{ passwordRules.upper ? "✓" : "✗" }}</span>
                    At least one uppercase letter
                  </li>
                  <li :class="{ 'rule-met': passwordRules.lower }">
                    <span class="rule-icon">{{ passwordRules.lower ? "✓" : "✗" }}</span>
                    At least one lowercase letter
                  </li>
                  <li :class="{ 'rule-met': passwordRules.digit }">
                    <span class="rule-icon">{{ passwordRules.digit ? "✓" : "✗" }}</span>
                    At least one number
                  </li>
                  <li :class="{ 'rule-met': passwordRules.special }">
                    <span class="rule-icon">{{ passwordRules.special ? "✓" : "✗" }}</span>
                    At least one special character
                  </li>
                </ul>

                <b-form-group class="mb-4">
                  <b-form-input
                    type="password"
                    v-model="$v.form.confirmNewPassword.$model"
                    placeholder="Confirm Password"
                    @keyup.enter="onSubmit()"
                    :state="$v.form.confirmNewPassword.$dirty ? !$v.form.confirmNewPassword.$error : null"
                  />
                  <b-form-invalid-feedback>Passwords must be identical.</b-form-invalid-feedback>
                </b-form-group>

                <b-button
                  type="submit"
                  variant="primary"
                  block
                  :disabled="submitted || $v.form.$invalid"
                >
                  <b-spinner small v-if="submitted" label="Loading..."></b-spinner>
                  <span v-else>Submit</span>
                </b-button>
              </b-form>

              <router-link to="/" class="d-inline-block mt-4">Back to login</router-link>
            </template>
          </div>
        </b-overlay>
      </b-col>
    </b-row>
  </b-container>
</template>

<script>
import { HTTP } from "../../utils/HTTP";
import { Helper } from "../../helpers";
import { validationMixin } from "vuelidate";
import { required, minLength, sameAs, helpers } from "vuelidate/lib/validators";
import Logo from "../../components/logo/logo.vue";

const hasUpperCase = helpers.regex( "hasUpperCase", /[A-Z]/ );
const hasLowerCase = helpers.regex( "hasLowerCase", /[a-z]/ );
const hasDigit = helpers.regex( "hasDigit", /[0-9]/ );
const hasSpecialChar = helpers.regex( "hasSpecialChar", /[^A-Za-z0-9]/ );

export default {
  name: "ResetPassword",
  mixins: [validationMixin],
  components: {
    Logo,
  },
  data () {
    return {
      logo: {
        width: "auto",
        height: "50px",
        class: "mt-2",
      },
      token: null,
      username: null,
      verifying: true,
      linkInvalid: false,
      submitted: false,
      form: {
        newPassword: null,
        confirmNewPassword: null,
      },
    };
  },
  validations: {
    form: {
      newPassword: {
        required,
        minLength: minLength( 10 ),
        hasUpperCase,
        hasLowerCase,
        hasDigit,
        hasSpecialChar,
      },
      confirmNewPassword: {
        required,
        sameAsPassword: sameAs( "newPassword" ),
      },
    },
  },
  computed: {
    passwordRules () {
      let pwd = this.form.newPassword || "";
      return {
        minLength: pwd.length >= 10,
        upper: /[A-Z]/.test( pwd ),
        lower: /[a-z]/.test( pwd ),
        digit: /[0-9]/.test( pwd ),
        special: /[^A-Za-z0-9]/.test( pwd ),
      };
    },
  },
  mounted () {
    this.token = this.$route.params.token;
    this.verifyResetToken();
  },
  methods: {
    async verifyResetToken () {
      if ( !this.token ) {
        this.verifying = false;
        this.linkInvalid = true;
        return;
      }
      try {
        let response = await HTTP( "post", "verify-reset-token", { token: this.token } );
        if ( response && response.data && response.data.valid ) {
          this.username = response.data.username;
        } else {
          this.linkInvalid = true;
        }
      } catch ( error ) {
        this.linkInvalid = true;
        console.log( "verifyResetToken Error", error );
      } finally {
        this.verifying = false;
      }
    },
    onSubmit () {
      this.$v.form.$touch();
      if ( this.$v.form.$anyError ) {
        return;
      }
      this.resetPassword();
    },
    async resetPassword () {
      this.submitted = true;
      try {
        let response = await HTTP( "post", "reset-password", {
          token: this.token,
          newPassword: this.form.newPassword,
        } );
        let data = response && response.data;
        if ( data && data.status ) {
          this.$router.push( {
            name: "login",
            path: "/",
            params: { successMsg: data.message || "Password created successfully." },
          } );
        } else {
          this.linkInvalid = true;
        }
      } catch ( error ) {
        Helper.makeToast( this.$bvToast, "danger", "Please Try Again Later" );
        console.log( "resetPassword Error", error );
      } finally {
        this.submitted = false;
      }
    },
  },
};
</script>

<style scoped>
.auth-card {
  float: none;
}
.password-rules {
  font-size: 0.85rem;
  color: #8a94a6;
  margin: 0.5rem 0 1.25rem;
}
.password-rules li {
  margin-bottom: 4px;
}
.password-rules .rule-icon {
  display: inline-block;
  width: 1.2em;
}
.password-rules li.rule-met {
  color: #2e7d4f;
}
</style>
